import Box from '@mui/material/Box';
import React from 'react';

import { EditablePartLocation, EditablePartLocationResult } from './EditablePartLocation';
import { PartLocationObject } from '../../../../../@types/part.type';
import { PrintLabelEntityTypes } from '../../../../../@types/print-label.type';
import { ServiceObject } from '../../../../../@types/service.type';
import { FieldGrid, FieldItem } from '../../../../../components/atoms/FieldGrid';
import { Panel } from '../../../../../components/atoms/Panel';
import { PriorityDot } from '../../../../../components/atoms/PriorityDot';
import { StatusBadge } from '../../../../../components/atoms/StatusBadge';
import { PrintLabel } from '../../../../../components/molecules/PintLabel';

export interface RepairJobDetailsPanelProps {
  service: ServiceObject | null;
  partSerial?: string;
  partTypeName?: string;
  partLocation?: PartLocationObject;
  canEditLocation?: boolean;
  onPartLocationChange?: (value: EditablePartLocationResult) => void;
  /** Optional header action (e.g. a "Link Job" button) rendered beside Print Label. */
  headerAction?: React.ReactNode;
  /**
   * Client/customer users must not see the allocated technician (primary
   * allocation). When true the Technician field is omitted.
   */
  isClient?: boolean;
}

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } catch {
    return '—';
  }
};

const formatSerialPartType = (serial?: string, partType?: string): string => {
  const parts = [serial, partType].filter(Boolean);
  return parts.length > 0 ? parts.join(' - ') : '—';
};

const formatClientContract = (clientCode?: string, contract?: string): string => {
  const parts = [clientCode, contract].filter(Boolean);
  return parts.length > 0 ? parts.join(' / ') : '—';
};

export const RepairJobDetailsPanel: React.FC<RepairJobDetailsPanelProps> = ({
  service,
  partSerial,
  partTypeName,
  partLocation,
  canEditLocation = false,
  onPartLocationChange,
  headerAction,
  isClient = false,
}) => {
  const statusName = service?.serviceStatus?.name ?? '';
  const priorityLabel = service?.priorityLabel ?? service?.priorityCode ?? '';
  const serviceTypeName = service?.serviceType?.[0]?.name ?? service?.serviceTypeCode ?? '—';

  const resolvedSerial = partSerial || service?.reportedSerialNumber || '';
  const resolvedPartType = partTypeName || service?.reportedModel || '';
  const serialPartType = formatSerialPartType(resolvedSerial, resolvedPartType);

  const clientContract = formatClientContract(
    service?.clientCode ?? undefined,
    service?.contract ?? undefined
  );

  const partId = service?.reportedSerialNumberPartId;

  return (
    <Panel
      title="Job Details"
      actions={
        headerAction || partId ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {headerAction}
            {partId ? (
              <PrintLabel entityId={partId} entityType={PrintLabelEntityTypes.Part} />
            ) : null}
          </Box>
        ) : undefined
      }
    >
      <FieldGrid columns={2}>
        <FieldItem label="Job Type" value={serviceTypeName} />
        <FieldItem label="Status" value={statusName ? <StatusBadge status={statusName} /> : '—'} />
        <FieldItem
          label="Priority"
          value={priorityLabel ? <PriorityDot priority={priorityLabel} /> : '—'}
        />
        <FieldItem label="Client / Contract" value={clientContract} />
        <FieldItem label="Created" value={formatDateTime(service?.createdAt)} />
        <FieldItem label="Closed At" value={formatDateTime(service?.closedAt)} />
        <FieldItem label="Logged By" value={service?.loggedBy ?? '—'} />
        {!isClient && (
          <FieldItem label="Technician" value={service?.primaryAllocation?.node?.name ?? '—'} />
        )}
        <FieldItem label="Serial - Part Type" value={serialPartType} fullWidth />
        <FieldItem
          label="Current Part Location"
          value={
            <EditablePartLocation
              partLocation={partLocation}
              isEditable={canEditLocation && Boolean(onPartLocationChange)}
              onSave={(result) => onPartLocationChange?.(result)}
            />
          }
          fullWidth
        />
      </FieldGrid>
    </Panel>
  );
};
